import type { Directive, DirectiveBinding } from 'vue';
import { usePermissionList } from '@/common/hooks/usePermissionList';

/**
 * v-permission 自定义指令：根据 permissionList 控制元素的显隐。
 *
 * 用法：
 *   - 单个权限码：v-permission="'analog:add'"
 *   - 多个权限码（满足其一即显示）：v-permission="['analog:add','analog:list']"
 *
 * 无权限时元素在 mounted 阶段从 DOM 移除。
 * permissionList 在登录后不变，故仅在校验绑定值时评估一次。
 *
 * 注意：非法绑定值（非 string / 非 string[]）默认保留元素，避免误删。
 */

/**
 * 校验指令绑定值是否通过权限检查。
 *
 * @param binding - 指令绑定对象
 * @returns 通过校验返回 true，未通过返回 false
 */
const checkPermission = (binding: DirectiveBinding): boolean => {
  const value = binding.value;
  const { hasPermission, hasAnyPermission } = usePermissionList();

  if (typeof value === 'string') {
    return hasPermission(value);
  }
  if (Array.isArray(value)) {
    return hasAnyPermission(value);
  }
  return true;
};

export const permissionDirective: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    if (!checkPermission(binding)) {
      el.parentNode?.removeChild(el);
    }
  },
  updated(el: HTMLElement, binding: DirectiveBinding) {
    // 绑定值动态变化时重新校验；仅支持移除（无法重新插入已移除节点）。
    // permissionList 会话内不变，主要应对 :v-permission 绑定值变更场景。
    if (!checkPermission(binding)) {
      el.parentNode?.removeChild(el);
    }
  },
};
